import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  SlidersHorizontal,
  HelpCircle,
  House,
  Settings as SettingsIcon,
  User,
  X,
} from 'lucide-react-native';
import { ComponentType, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { HomeSwitcher } from '@/components/HomeSwitcher';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { goTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { TOOLS_CATALOG, getToolByKey, parseToolsKeys } from '@/lib/tools-menu';

/**
 * Slide-up Tools menu launched from the AppHeader. One central place to
 * reach every secondary surface — Schedule, Inventory, Expenses,
 * Contacts, Goals, Media, Timeline, Find a Pro — plus the
 * Account / Home settings rows beneath. Replaces the prior pattern of
 * hunting for tools across Settings + Quick Actions + the tab bar.
 *
 * Design:
 *   • Bottom-sheet modal with rounded top corners — consistent with
 *     DocumentPreview, ReschedulePicker, and other modals in the app.
 *   • 4×2 grid of tiles with soft-tinted icon chips, label beneath.
 *   • Divider, then full-width settings rows for Account + Home.
 *   • Tap backdrop or X to dismiss; navigation also dismisses on tap.
 *   • Uses `goTo` so the destination route replaces the current screen
 *     in history rather than stacking — keeps the back history clean
 *     when the user hops between tools.
 */

// Wave 62: the grid is user-curated — TOOLS_CATALOG + the saved key
// list in property.tools_menu_keys_json (edited at /tools-menu)
// replace the old hard-coded array. Defaults: Smart scan leads,
// Quotes in, Inventory out.

export function ToolsMenu({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const router = useRouter();
  const db = useSQLiteContext();
  const insets = useSafeAreaInsets();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [showAllTools, setShowAllTools] = useState(false);
  // User-curated grid keys — refreshed each open so edits from
  // /tools-menu show up immediately.
  const [toolKeys, setToolKeys] = useState<string[] | null>(null);
  useEffect(() => {
    if (!open) { setShowAllTools(false); return; }
    (async () => {
      const prop = await getActiveProperty(db);
      setToolKeys(parseToolsKeys(prop?.tools_menu_keys_json));
    })();
  }, [open, db]);
  const tools = (toolKeys ?? parseToolsKeys(null))
    .map((k) => getToolByKey(k))
    .filter((t): t is NonNullable<typeof t> => t != null);
  // Android's gesture / nav bar eats taps along the bottom edge — pad
  // the sheet so the last row (Contact & support) never sits under it.
  const bottomPad = Math.max(28, insets.bottom + 24);

  const nav = (href: string) => {
    onClose();
    // Defer the actual navigation a frame so the close animation can
    // start; otherwise the destination screen mounts while the modal
    // backdrop is still fading, which can flicker on slower devices.
    setTimeout(() => goTo(router, href), 50);
  };

  /** Close this menu and immediately open the HomeSwitcher. Stacking
   *  two modals would compound the backdrops awkwardly, so we hand off
   *  cleanly: dismiss Tools, wait for it to fade, then open the
   *  switcher. */
  const openSwitcher = () => {
    onClose();
    setTimeout(() => setSwitcherOpen(true), 50);
  };

  return (
    <>
    <Modal
      animationType="fade"
      transparent
      visible={open}
      onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        {/* stopPropagation: tapping inside the sheet shouldn't close it */}
        <Pressable style={[styles.sheet, { paddingBottom: bottomPad }]} onPress={(e) => e.stopPropagation()}>
          <View style={styles.handle} />

          <View style={styles.head}>
            <Text style={styles.title}>Tools</Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close menu"
              style={({ pressed }) => [
                styles.closeBtn,
                pressed && { opacity: 0.85 },
              ]}>
              <X size={15} color={Palette.inkSoft} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={styles.grid}>
            {tools.map((t) => (
              <Pressable
                key={t.key}
                onPress={() => nav(t.href)}
                accessibilityLabel={`Open ${t.label}`}
                style={({ pressed }) => [
                  styles.tile,
                  pressed && { transform: [{ scale: 0.97 }], opacity: 0.92 },
                ]}>
                <View style={[styles.tileIcon, { backgroundColor: t.chipBg }]}>
                  <t.Icon size={20} color={t.iconColor} strokeWidth={1.7} />
                </View>
                <Text style={styles.tileLabel} numberOfLines={1}>
                  {t.label}
                </Text>
              </Pressable>
            ))}
          </View>

          {/* Wave 71 (user call): "View all tools" expands the REST of
              the catalog right here in the sheet — no page hop. The
              customize link rides along once expanded. */}
          {(() => {
            const shown = new Set(tools.map((t) => t.key));
            const rest = TOOLS_CATALOG.filter((t) => !shown.has(t.key));
            if (rest.length === 0) return null;
            return (
              <>
                <Pressable
                  onPress={() => setShowAllTools((v) => !v)}
                  accessibilityLabel={showAllTools ? 'Hide extra tools' : 'View all tools'}
                  style={({ pressed }) => [styles.allToolsRow, pressed && { opacity: 0.8 }]}>
                  {showAllTools ? (
                    <ChevronUp size={13} color={Palette.forest} strokeWidth={2.2} />
                  ) : (
                    <ChevronDown size={13} color={Palette.forest} strokeWidth={2.2} />
                  )}
                  <Text style={styles.allToolsText}>
                    {showAllTools ? 'Fewer tools' : `View all tools · ${rest.length} more`}
                  </Text>
                </Pressable>
                {showAllTools ? (
                  <>
                    <View style={styles.grid}>
                      {rest.map((t) => (
                        <Pressable
                          key={t.key}
                          onPress={() => nav(t.href)}
                          accessibilityLabel={`Open ${t.label}`}
                          style={({ pressed }) => [
                            styles.tile,
                            pressed && { transform: [{ scale: 0.97 }], opacity: 0.92 },
                          ]}>
                          <View style={[styles.tileIcon, { backgroundColor: t.chipBg }]}>
                            <t.Icon size={20} color={t.iconColor} strokeWidth={1.7} />
                          </View>
                          <Text style={styles.tileLabel} numberOfLines={1}>
                            {t.label}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                    <Pressable
                      onPress={() => nav('/tools-menu')}
                      accessibilityLabel="Customize this grid"
                      style={({ pressed }) => [styles.allToolsRow, pressed && { opacity: 0.8 }]}>
                      <SlidersHorizontal size={13} color={Palette.inkSoft} strokeWidth={2} />
                      <Text style={[styles.allToolsText, { color: Palette.inkSoft }]}>
                        Customize this grid
                      </Text>
                    </Pressable>
                  </>
                ) : null}
              </>
            );
          })()}

          {/* Switch home — promoted to a prominent forest-tinted card
              right under the tools grid. Multi-home users open this
              constantly, so it's faster to spot than a settings-style
              text row. Single tap opens the HomeSwitcher (with the
              "+ Add a home" CTA inside). */}
          <Pressable
            onPress={openSwitcher}
            accessibilityLabel="Switch home"
            style={({ pressed }) => [
              styles.switchCard,
              pressed && { opacity: 0.92, transform: [{ scale: 0.99 }] },
            ]}>
            <View style={styles.switchCardIcon}>
              <ChevronsUpDown size={18} color={Palette.cream} strokeWidth={2.2} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.switchCardLabel}>Switch home</Text>
              <Text style={styles.switchCardSub}>
                Jump between your homes or add a new one
              </Text>
            </View>
          </Pressable>

          <View style={styles.divider} />

          <Text style={styles.sectionLabel}>SETTINGS</Text>

          {/* Account — dedicated /account page (preview of the future
              account dashboard). Settings still hosts the per-device
              preferences; this row is for identity / billing / cloud
              once those land. */}
          <Pressable
            onPress={() => nav('/account')}
            accessibilityLabel="Open account"
            style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}>
            <View style={[styles.rowIcon, { backgroundColor: '#EAF0EA' }]}>
              <User size={16} color={Palette.forest} strokeWidth={1.9} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowLabel}>Account</Text>
              <Text style={styles.rowSub}>Profile, sign-in, cloud sync</Text>
            </View>
          </Pressable>

          {/* Home settings — per-property attributes for the active home. */}
          <Pressable
            onPress={() => nav('/property/edit')}
            accessibilityLabel="Open home settings"
            style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}>
            <View style={[styles.rowIcon, { backgroundColor: '#F3DDAE' }]}>
              <House size={16} color={Palette.honeyDeep} strokeWidth={1.9} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowLabel}>Home settings</Text>
              <Text style={styles.rowSub}>
                Address, photo, attributes, replan
              </Text>
            </View>
          </Pressable>

          {/* App settings — preferences, toolbar, notifications, etc.
              Same destination as Account for now but conceptually
              distinct: this is the existing Settings screen content. */}
          <Pressable
            onPress={() => nav('/(tabs)/settings')}
            accessibilityLabel="Open app settings"
            style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}>
            <View style={[styles.rowIcon, { backgroundColor: '#DCE7E1' }]}>
              <SettingsIcon size={16} color={Palette.moss} strokeWidth={1.9} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowLabel}>App settings</Text>
              <Text style={styles.rowSub}>
                Preferences, notifications, layout
              </Text>
            </View>
          </Pressable>

          {/* Contact / Support — new dedicated page with email link,
              version info, FAQs, and roadmap notes. */}
          <Pressable
            onPress={() => nav('/support')}
            accessibilityLabel="Open contact and support"
            style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}>
            <View style={[styles.rowIcon, { backgroundColor: 'rgba(176,95,60,0.15)' }]}>
              <HelpCircle size={16} color={Palette.clay} strokeWidth={1.9} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowLabel}>Contact & support</Text>
              <Text style={styles.rowSub}>
                Reach the team, version, roadmap
              </Text>
            </View>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
    <HomeSwitcher
      visible={switcherOpen}
      onClose={() => setSwitcherOpen(false)}
    />
    </>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(42,37,31,0.42)',
    // Anchor the sheet to the bottom of the viewport. Consistent with
    // the other modals in the app (DocumentPreview, ReschedulePicker).
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: Spacing.four,
    paddingTop: 12,
    paddingBottom: 28,
    width: '100%',
    // Cap the sheet width on desktop so it doesn't span huge monitors.
    // The viewport's centered column shell already handles most of this,
    // but the modal renders outside that column so we set the cap here.
    maxWidth: 520,
    alignSelf: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -6 },
    shadowOpacity: 0.18,
    shadowRadius: 22,
    elevation: 12,
  },
  // Small "grab handle" indicator at the top — universal mobile signal
  // that this surface can be dismissed.
  handle: {
    alignSelf: 'center',
    width: 44,
    height: 4,
    borderRadius: 2,
    backgroundColor: Palette.sandDeep,
    marginBottom: 12,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Spacing.four,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 22,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // 4-column grid. flex-basis math: (100% - 3*gap) / 4. With gap 10 in
  // the parent, each tile claims roughly 22.5% of the width.
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  tile: {
    width: '22.75%',
    aspectRatio: 1,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingHorizontal: 6,
    paddingVertical: 10,
  },
  tileIcon: {
    width: 36,
    height: 36,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 12,
    color: Palette.ink,
    letterSpacing: 0.1,
    textAlign: 'center',
  },

  // Prominent forest-tinted card for Switch home — sits between the
  // tools grid and the settings list, intentionally louder than the
  // text rows because multi-home users tap this many times a day.
  switchCard: {
    marginTop: Spacing.four,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: Radius.lg,
    backgroundColor: Palette.forest,
  },
  switchCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(251,246,234,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  switchCardLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 15,
    color: Palette.cream,
    letterSpacing: -0.1,
  },
  switchCardSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: 'rgba(251,246,234,0.78)',
    lineHeight: 16,
  },
  divider: {
    height: 1,
    backgroundColor: Palette.line,
    marginVertical: Spacing.four,
  },
  allToolsRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6,
    paddingVertical: 9,
    marginTop: 2,
  },
  allToolsText: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest,
    letterSpacing: 0.2,
  },
  sectionLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 1,
    marginBottom: 8,
    marginLeft: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  rowIcon: {
    width: 36,
    height: 36,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  rowSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    marginTop: 1,
  },
});
